import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { track } from "@/lib/track";
import log from "@/lib/log";
import { attributionPayload } from "@/lib/attribution";

const visibleFor = (questions, answers) => questions.filter((q) => !q.conditional || q.conditional(answers));

const RESULT_ERROR = "We couldn't prepare your results just now. Please check your connection and try again.";

export function useAssessment(pathway) {
  const questions = pathway.questions;
  const [step, setStep] = useState("intro");
  const [answers, setAnswers] = useState({});
  const [assessmentId, setAssessmentId] = useState(null);
  const [result, setResult] = useState(null);
  const [resultError, setResultError] = useState("");

  // Refs mirror the latest values so async callbacks (timers, in-flight requests) never read stale state.
  const answersRef = useRef(answers);
  const assessmentIdRef = useRef(assessmentId);
  const resultRef = useRef(result);
  const completedRef = useRef(false);
  answersRef.current = answers;
  assessmentIdRef.current = assessmentId;
  resultRef.current = result;

  // Pre-start a server-side assessment for this pathway; reset everything if the pathway changes.
  // The ref guard survives StrictMode's simulated remount and drops late responses from a previous pathway;
  // sessionStorage lets a page reload (e.g. a CDN challenge) reuse the same started assessment instead of orphaning one.
  const startedForRef = useRef(null);
  useEffect(() => {
    if (startedForRef.current === pathway.key) return;
    startedForRef.current = pathway.key;
    setStep("intro");
    setAnswers({});
    setResult(null);
    setResultError("");
    completedRef.current = false;
    const storageKey = `lw_assessment_${pathway.key}`;
    const reused = sessionStorage.getItem(storageKey);
    setAssessmentId(reused || null);
    if (reused) return;
    api.post("/assessments/start", { attribution: attributionPayload(), pathway: pathway.key })
      .then((r) => {
        if (startedForRef.current !== pathway.key) return;
        sessionStorage.setItem(storageKey, r.data.assessment_id);
        setAssessmentId(r.data.assessment_id);
      })
      .catch((e) => log.warn("Could not pre-start assessment; will retry on completion", e));
  }, [pathway]);

  const visibleQuestions = useMemo(() => visibleFor(questions, answers), [questions, answers]);
  const question = typeof step === "number" ? visibleQuestions[step] : null;
  const isLast = typeof step === "number" && step === visibleQuestions.length - 1;
  const progress = typeof step === "number" ? Math.round(((step + 1) / visibleQuestions.length) * 100) : 0;

  const start = useCallback(() => {
    track("checkup_started", pathway.key);
    setStep(0);
    setTimeout(() => {
      document.querySelector('[data-testid="assessment-progress-bar"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
  }, [pathway]);

  const finish = useCallback(async () => {
    setStep("results");
    setResultError("");
    window.scrollTo({ top: 0, behavior: "instant" });
    if (completedRef.current && resultRef.current) return;
    completedRef.current = true;
    try {
      let id = assessmentIdRef.current;
      if (!id) {
        const s = await api.post("/assessments/start", { attribution: attributionPayload(), pathway: pathway.key });
        id = s.data.assessment_id;
        setAssessmentId(id);
      }
      const r = await api.post(`/assessments/${id}/complete`, { answers: answersRef.current });
      sessionStorage.removeItem(`lw_assessment_${pathway.key}`);
      setResult(r.data);
      track("checkup_completed", pathway.key);
    } catch (e) {
      completedRef.current = false;
      log.warn("Assessment completion failed", e);
      setResultError(RESULT_ERROR);
    }
  }, [pathway]);

  const selectOption = useCallback((q, value) => {
    setAnswers((prev) => {
      const next = { ...prev, [q.id]: value };
      if (q.id === "q4" && value !== "yes") delete next.q4_coverage;
      return next;
    });
    setTimeout(() => {
      const vq = visibleFor(questions, answersRef.current);
      const idx = vq.findIndex((x) => x.id === q.id);
      if (idx === -1) return;
      if (idx < vq.length - 1) setStep(idx + 1);
      else finish();
    }, 300);
  }, [questions, finish]);

  const toggleMulti = useCallback((q, opt) => {
    setAnswers((prev) => {
      const cur = prev[q.id] || [];
      if (cur.includes(opt.value)) return { ...prev, [q.id]: cur.filter((v) => v !== opt.value) };
      if (opt.exclusive) return { ...prev, [q.id]: [opt.value] };
      const exclusives = q.options.filter((o) => o.exclusive).map((o) => o.value);
      return { ...prev, [q.id]: [...cur.filter((v) => !exclusives.includes(v)), opt.value] };
    });
  }, []);

  const back = useCallback(() => setStep((s) => (s === 0 ? "intro" : s - 1)), []);
  const next = useCallback(() => (isLast ? finish() : setStep((s) => s + 1)), [isLast, finish]);

  return { step, answers, assessmentId, result, resultError, question, visibleQuestions, isLast, progress, start, finish, selectOption, toggleMulti, back, next };
}
